/**
 * The states a piece of work moves through, and what the client is told.
 *
 * Three vocabularies meet here, which is why they live in one file rather than
 * three: a sprint's lifecycle, the internal Board column, and the single status
 * the client sees. The last is DERIVED from the first two — it is never stored,
 * because a stored copy is a copy that can disagree with the board.
 */
/**
 * Scope is fixed: the sprint has been locked, and from here it moves only
 * through an agreed change of plan.
 *
 * Every refusal that used to read `state !== 'planning'` means this. Written as
 * a predicate so that adding a fifth state is one edit here rather than a hunt
 * through nine call sites — which is exactly how `started` came to be missed
 * when it was added.
 */
export function isScopeFrozen(state) {
    return state === 'locked' || state === 'started' || state === 'released';
}
/** Work is under way. Exactly one sprint may be in this state at a time. */
export function isRunning(state) {
    return state === 'started';
}
/** Locked or started: the scope is frozen but the week is not over. */
export function isCommitted(state) {
    return state === 'locked' || state === 'started';
}
/**
 * The one status the client sees, from the two facts that decide it.
 *
 * `sprintState` is null when the request sits in no sprint at all.
 *
 * The bug this replaces: the old table keyed off the board column alone, so
 * every item in a locked sprint still sitting in Triage or Ready read
 * "Received" to the client — the same word as something posted a minute ago and
 * never looked at. Goal 2 is that the client can see what is queued and what is
 * committed; a column-only mapping cannot express the difference, because being
 * in a sprint is not a column.
 */
export function clientStatusOf(boardColumn, sprintState, needsReview) {
    // Where the work has reached beats where it was planned to go. A request
    // being tested is "Testing" whether or not its sprint has been released.
    if (boardColumn === 'done')
        return 'released';
    if (boardColumn === 'review' || boardColumn === 'staging')
        return 'testing';
    if (boardColumn === 'in_progress')
        return 'in_progress';
    // Committed to a sprint whose scope is fixed: a date has been promised.
    if (sprintState !== null && isCommitted(sprintState))
        return 'scheduled';
    // Nobody has confirmed what this is yet, so no promise can be made about it.
    if (needsReview)
        return 'received';
    return 'queued';
}
/**
 * What the column alone says, for the places that genuinely only have a column.
 *
 * Two kinds of caller need this. One is describing a COLUMN MOVE rather than a
 * request — "moved to In progress" in the activity trail — where the sprint is
 * not part of what happened. The other is a list that never joined sprints.
 *
 * It cannot tell Queued from Scheduled, because that is not a property of the
 * column. Prefer `clientStatusOf` anywhere the sprint is in hand; reaching for
 * this one to avoid a join is how the original bug happened.
 */
export function clientStatusOfColumn(boardColumn) {
    return clientStatusOf(boardColumn, null, false);
}
//# sourceMappingURL=delivery.js.map